import { MAX_ELEMIS_INSTALMENTS } from '../../../services/evenMonthlyTerm';
import type { EvenMonthlyDerivation } from '../../../services/evenMonthlyTerm';
import { isUnusableEnteredDeposit, type AppliedDeposit } from '../../../services/appliedDeposit';
import { formatMinorAmount } from '../../../utils/formatScheduleCurrency';
import type { LeadRejectionKind, CollectionRoutingAxis } from './derive';

export interface TermWarningCopy {
    readonly heading: string;
    readonly cause: string;
    readonly consequence: string;
    readonly remedy: string;
}

const TERM_WARNING_HEADING = 'Schedule term not derived';
const TERM_WARNING_CAUSE =
    "The plan's term could not be worked out from this quote, because no service or optional extra has a date.";
const TERM_WARNING_REMEDY = 'Add or adjust the service dates to see the correct schedule.';

// No default arm: a new variant must be a compile error here, not a silent missing surface.
export const termWarningCopy = (derivation: EvenMonthlyDerivation): TermWarningCopy | null => {
    switch (derivation.kind) {
        case 'offerable':
            return null;
        case 'not-offerable':
            return null;
        case 'underivable':
            return {
                heading: TERM_WARNING_HEADING,
                cause: TERM_WARNING_CAUSE,
                consequence:
                    "This schedule is being calculated over 12 monthly payments, which may not match the plan's actual term.",
                remedy: TERM_WARNING_REMEDY,
            };
        case 'offerable-by-default': {
            const n = derivation.numInstalments;
            return {
                heading: TERM_WARNING_HEADING,
                cause: TERM_WARNING_CAUSE,
                consequence:
                    `This schedule is being calculated over this product's default of ${n} monthly ` +
                    `${n === 1 ? 'payment' : 'payments'}, which may not match the plan's actual term.`,
                remedy: TERM_WARNING_REMEDY,
            };
        }
    }
};

export const unpricedItemsCopy = (itemNames: readonly string[]): string =>
    `The gross price is missing, or is below the net price, for: ${itemNames.join(', ')}. The ` +
    'payment schedule cannot be previewed until these are priced. Contact support.';

export const leadRejectionCopy = (
    kind: LeadRejectionKind,
    months: number,
    bounds: { min: number; max: number },
): string => {
    switch (kind) {
        case 'non-integer':
            return (
                `This quote's collection lead of ${months} is not a whole number of months. ` +
                `Enter a whole number between ${bounds.min} and ${bounds.max} months to preview ` +
                'and confirm a schedule.'
            );
        case 'out-of-range':
            return (
                `This quote's collection lead of ${months} month(s) is outside the range this ` +
                `product now allows (${bounds.min} to ${bounds.max} months). Enter a lead within ` +
                'that range to preview and confirm a schedule.'
            );
    }
};

export const resolveLeadRejectionMessage = (
    kind: LeadRejectionKind | null,
    months: number | undefined,
    bounds: { min: number; max: number } | null,
): string | null =>
    kind !== null && bounds !== null && months !== undefined ? leadRejectionCopy(kind, months, bounds) : null;

export const unavailableReasonText = (
    label: string,
    derivation: Extract<EvenMonthlyDerivation, { kind: 'not-offerable' }>,
): string => {
    switch (derivation.reason) {
        case 'below-floor':
            if (derivation.numInstalments > 0) {
                return (
                    `${label} is unavailable: this plan would be collected over ` +
                    `${derivation.numInstalments} monthly payments, and this product requires ` +
                    `at least ${derivation.floor}.`
                );
            }
            return derivation.termMonths !== undefined && derivation.termMonths > 0
                ? `${label} is unavailable: this plan's term is ${derivation.termMonths} ` +
                      `${derivation.termMonths === 1 ? 'month' : 'months'}, and this product ` +
                      'requires the collection window to finish further ahead of the final ' +
                      'service than that allows, so there are no instalments left to collect over.'
                : `${label} is unavailable: every priced service or optional extra that carries a date ` +
                      'falls due within the next month, or has already passed, so there is no ' +
                      'term to collect over.';
        case 'above-transport-max': {
            // No `termMonths` means the count came from the product default, so it is not a term.
            const clause =
                derivation.termMonths === undefined
                    ? `this plan would be collected over ${derivation.numInstalments} monthly payments`
                    : `the plan's term works out at ${derivation.numInstalments} monthly payments`;
            return (
                `${label} is unavailable: ${clause}, beyond the ` +
                `${MAX_ELEMIS_INSTALMENTS} a payment schedule can be calculated over.`
            );
        }
        default: {
            // No default arm: a new variant must be a compile error here, not a silent missing surface.
            const unreachable: never = derivation.reason;
            return unreachable;
        }
    }
};

export interface ReadOnlyDepositStatement {
    readonly lead: string;
    readonly figure?: string;
}

/** The non-null axes: the form and its notices only ever render for one. */
export type DealerCollectedAxis = Exclude<CollectionRoutingAxis, null>;

export interface DealerCollectedPaymentCopy {
    readonly legend: string;
    readonly elemisLabel: string;
    readonly dealerLabel: string;
    readonly amountLabel: string;
}

export interface LinkDeliveryCopy {
    readonly emailLabel: (email: string) => string;
    readonly smsLabel: (mobile: string) => string;
    readonly emailLabelMissing: string;
    readonly smsLabelMissing: string;
    readonly noEmailHint: string;
    readonly noMobileHint: string;
    readonly offSystemNote: string;
}

export type PaymentCollectionCopyScenario =
    | { kind: 'radio'; axis: DealerCollectedAxis }
    | { kind: 'motorplan_only' }
    | { kind: 'link_delivery' };

const ELEMIS_OPTION_LABEL = 'Card Payment via MotorPlan Payments Ltd';
const DEALER_OPTION_LABEL = 'Direct to Dealer';

const LINK_DELIVERY_COPY: LinkDeliveryCopy = {
    emailLabel: (email) => `Email the payment link to ${email}`,
    smsLabel: (mobile) => `Text the payment link to ${mobile}`,
    emailLabelMissing: 'Email the payment link',
    smsLabelMissing: 'Text the payment link',
    noEmailHint: 'No email address on the customer record',
    noMobileHint: 'No mobile number on the customer record',
    offSystemNote:
        'The customer will receive a link to complete the MotorPlan payment process themselves, outside this system.',
};

export function paymentCollectionCopy(scenario: { kind: 'radio'; axis: DealerCollectedAxis }): DealerCollectedPaymentCopy;
export function paymentCollectionCopy(scenario: { kind: 'motorplan_only' }): { note: string };
export function paymentCollectionCopy(scenario: { kind: 'link_delivery' }): LinkDeliveryCopy;
export function paymentCollectionCopy(
    scenario: PaymentCollectionCopyScenario,
): DealerCollectedPaymentCopy | { note: string } | LinkDeliveryCopy {
    switch (scenario.kind) {
        case 'radio':
            return {
                legend: scenario.axis === 'deposit' ? 'Who collects the deposit?' : 'Who collects the payment?',
                elemisLabel: ELEMIS_OPTION_LABEL,
                dealerLabel: DEALER_OPTION_LABEL,
                amountLabel: 'Amount collected',
            };
        case 'motorplan_only':
            return { note: 'MotorPlan will continue the collection process.' };
        case 'link_delivery':
            return LINK_DELIVERY_COPY;
    }
}

export const readOnlyDepositStatement = (
    applied: AppliedDeposit | undefined,
    currency: string,
): ReadOnlyDepositStatement | null => {
    if (applied === undefined || applied.kind === 'none') {
        return null;
    }
    if (isUnusableEnteredDeposit(applied)) {
        return { lead: 'Deposit entered for this quote (amount unavailable)' };
    }
    if (applied.kind === 'unstated') {
        return { lead: 'Deposit set by the payment schedule' };
    }
    return {
        lead:
            applied.source === 'schedule'
                ? 'Deposit set by the payment schedule'
                : 'Deposit entered for this quote',
        figure: formatMinorAmount(applied.amountMinor, currency),
    };
};

/** A fixed deposit is stated, not offered — the figure is never editable, so this reads
 *  as a fact about the product rather than an applied value. */
export const fixedDepositStatement = (amountMinor: number, currency: string): ReadOnlyDepositStatement => ({
    lead: 'Deposit for this product',
    figure: formatMinorAmount(amountMinor, currency),
});

export const SCHEDULE_SUGGESTED_DEPOSIT_HINT =
    'Deposit suggested by the payment schedule. Enter an amount to set a different deposit.';

export const fixedDepositCeilingCopy = (
    fixedAmountMinor: number,
    ceilingExclusiveMinor: number,
    naturalCeilingLabel: string,
    currency: string,
): string =>
    `This product's deposit of ${formatMinorAmount(fixedAmountMinor, currency)} must be less than ` +
    `${naturalCeilingLabel} (${formatMinorAmount(ceilingExclusiveMinor, currency)}) to preview the ` +
    "schedule. Choose a different payment method, or ask an administrator to review this product's " +
    'deposit settings.';
